<template>
  <div class="audit-log-report">
    <!-- Control Buttons -->
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">Audit Log Report</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ auditLogs.length }} Records
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 2.5rem; height: 2.5rem" />
      <p class="mt-3 font-weight-medium">{{ loadingText }}</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h5 class="text-danger font-weight-medium">{{ error }}</h5>
    </div>

    <!-- Report Content -->
    <div v-else class="report-page">
      <!-- Header -->
      <div class="report-header-modern mb-4">
        <div class="d-flex align-items-center">
          <div class="logo-wrapper mr-3" v-if="schoolLogo">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img" />
          </div>
          <div class="header-text-block">
            <h2 class="school-name-full mb-0">
              {{ organizationName }}
            </h2>
            <div class="report-info-line mt-1">
              <span class="report-name-text">Audit Log Report</span>
              <span class="date-range-text">
                Generated On: <strong>{{ currentDateTime }}</strong>
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Stats Summary -->
      <div class="balance-summary mb-1">
        <div class="balance-card">
          <span class="label">Total Logs:</span>
          <span class="value text-primary">{{ auditLogs.length }}</span>
        </div>
      </div>

      <!-- Audit Log Table -->
      <div class="table-container">
        <table v-if="auditLogs.length > 0" class="report-table">
          <thead>
            <tr>
              <th class="text-center" style="width: 5%">S.NO</th>
              <th style="width: 15%">USER</th>
              <th style="width: 15%">FORM</th>
              <th class="text-center" style="width: 12%">ACTION</th>
              <th style="width: 15%">ENTRY DATE</th>
              <th>NOTE</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in auditLogs" :key="item.id || index">
              <td class="text-center">{{ index + 1 }}</td>
              <td>
                <div class="d-flex flex-column">
                  <span class="font-weight-bold">{{
                    item.meta_user || item.userName || item.user || "N/A"
                  }}</span>
                  <small v-if="item.userID" class="text-muted"
                    >ID: {{ item.userID }}</small
                  >
                </div>
              </td>
              <td class="font-weight-bold">{{ formatFormName(item.form) }}</td>
              <td class="text-center">
                <span class="action-badge" :class="getActionClass(item.action)">
                  {{ item.action || "N/A" }}
                </span>
              </td>
              <td>{{ formatDate(item.entryDate) }}</td>
              <td class="note-cell">{{ item.note || "N/A" }}</td>
            </tr>
          </tbody>
        </table>

        <!-- No Data -->
        <div v-else class="text-center py-5">
          <h6 class="text-muted">No audit log data available</h6>
        </div>
      </div>
      <!-- Footer -->
      <div class="page-footer">
        <small
          ><strong>Generated:</strong> {{ currentDateTime }} |
          <strong>{{ organizationName }}</strong></small
        >
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner } from "bootstrap-vue";
import axios from "axios";

export default {
  name: "AuditLogPrint",
  components: {
    BButton,
    BSpinner,
  },
  data() {
    return {
      loading: false,
      loadingText: "Loading audit log...",
      error: null,
      auditLogs: [],
      organizationName: "MySkool",
      schoolLogo: null,
    };
  },
  computed: {
    currentDateTime() {
      const now = new Date();
      return now.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    initReport() {
      const query = this.$route.query;
      this.organizationName =
        (this.$store.state.currentBranch &&
          this.$store.state.currentBranch.branch) ||
        "MySkool";
      this.schoolLogo =
        (this.$store.state.currentBranch &&
          this.$store.state.currentBranch.logo) ||
        null;

      this.loadCampusInfo();
      this.loadReportData();
    },

    async loadCampusInfo() {
      const db = this.$route.query.db || this.$store.state.userData?.db;
      const cID = this.$route.query.cID || this.$route.query.cId || 0;

      try {
        const url = `https://apiv26.myskool.app/api/campuses`;
        const response = await axios.get(url, {
          params: { db, cId: cID },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (
          response.data?.status === "success" &&
          response.data?.data?.length > 0
        ) {
          const info =
            response.data.data.find((c) => c.id == cID) ||
            response.data.data[0];
          this.organizationName = info.name;
          this.schoolLogo = info.logo;
        }
      } catch (e) {
        console.error("Failed to load campus info:", e);
      }
    },
    async loadReportData() {
      try {
        this.loading = true;
        this.error = null;

        const db = this.$route.query.db || this.$store.state.userData?.db;
        const token = this.$store.state.userData?.token;

        if (!db || !token) {
          this.error = "Missing authentication. Please log in again.";
          return;
        }

        const url = `${this.$store.state.domain || ""}AuditLog/GetByPagination`;
        const query = this.$route.query;

        const response = await axios.get(url, {
          params: {
            db,
            cID: query.cID || query.cId || this.$store.state.userData?.cId,
            page: 1,
            pageSize: 1000,
            userID: query.userID,
            startDate: query.startDate,
            endDate: query.endDate,
            search: query.search,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const payload = response?.data ?? response ?? {};
        const container = payload?.data;
        const list = Array.isArray(container?.data)
          ? container.data
          : Array.isArray(container)
          ? container
          : Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload)
          ? payload
          : [];

        this.auditLogs = list;
      } catch (err) {
        console.error("Failed to load audit log:", err);
        this.error =
          err.response?.data?.message ||
          "Failed to load audit log. Please try again.";
      } finally {
        this.loading = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return "N/A";
      try {
        return new Date(dateString).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch (e) {
        return dateString;
      }
    },

    formatFormName(form) {
      if (!form) return "N/A";
      return form.replace(/\b\w/g, (l) => l.toUpperCase());
    },

    getActionClass(action) {
      if (!action) return "action-default";
      const act = action.toLowerCase();
      if (act.includes("delete")) return "action-danger";
      if (act.includes("create") || act.includes("add"))
        return "action-success";
      if (act.includes("update") || act.includes("edit"))
        return "action-warning";
      if (act.includes("view") || act.includes("read")) return "action-info";
      return "action-default";
    },

    print() {
      window.print();
    },

    close() {
      window.close();
      if (!window.closed) this.$router.go(-1);
    },
  },
};
</script>

<style lang="scss" scoped>
.audit-log-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: "Outfit", "Inter", sans-serif;
  color: #2d3436;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
    letter-spacing: -0.5px;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  span {
    margin-left: 4px;
  }

  &:active {
    transform: scale(0.96);
  }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;

  &:hover {
    background: linear-gradient(135deg, #5b4cc4 0%, #8c82eb 100%);
    box-shadow: 0 6px 20px rgba(108, 92, 231, 0.35);
    transform: translateY(-1px);
  }
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;

  &:hover {
    background: #fdfdfd;
    color: #e74c3c;
    border-color: #fab1a0;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }
}

.report-page {
  background: #ffffff;
  padding: 3rem;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  width: 98%;
  max-width: 1400px;
  margin: 30px auto;
  box-sizing: border-box;
}

.report-header-modern {
  border-bottom: 2px solid #333;
  padding-bottom: 20px;

  .school-logo-img {
    height: 80px;
    width: auto;
    object-fit: contain;
  }

  .school-name-full {
    font-size: 1.7rem;
    font-weight: 800;
    color: #000;
    text-transform: uppercase;
    letter-spacing: -0.5px;
  }

  .report-info-line {
    font-size: 1.1rem;
    color: #444;
    display: flex;
    align-items: center;

    .report-name-text {
      font-weight: 700;
      border-right: 2px solid #ddd;
      padding-right: 15px;
      margin-right: 15px;
    }
  }
}

.balance-summary {
  display: flex;
  justify-content: center;
  gap: 3rem;
  padding: 1.25rem;
  background: #f8f9fa;
  border-radius: 12px;
  margin: 1.5rem 0;
  border: 1px solid #eee;

  .balance-card {
    display: flex;
    flex-direction: column;
    align-items: center;

    .label {
      font-size: 0.8rem;
      font-weight: bold;
      color: #636e72;
      text-transform: uppercase;
      margin-bottom: 4px;
    }

    .value {
      font-size: 1.4rem;
      font-weight: 800;
    }
  }
}

.table-container {
  overflow-x: auto;
}

.report-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  border: 1px solid #dee2e6;
  font-size: 0.9rem;

  th,
  td {
    border: 1px solid #dee2e6;
    padding: 10px;
    vertical-align: middle;
  }

  thead {
    background: #2c3e50;
    color: white;

    th {
      font-weight: 700;
      text-transform: uppercase;
      font-size: 0.8rem;
      letter-spacing: 0.5px;
    }
  }

  tbody tr:nth-child(even) {
    background-color: #f8f9fa;
  }
}

.action-badge {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;

  &.action-danger {
    background: #f8d7da;
    color: #721c24;
  }
  &.action-success {
    background: #d4edda;
    color: #155724;
  }
  &.action-warning {
    background: #fff3cd;
    color: #856404;
  }
  &.action-info {
    background: #d1ecf1;
    color: #0c5460;
  }
  &.action-default {
    background: #e9ecef;
    color: #495057;
  }
}

.note-cell {
  max-width: 320px;
  word-wrap: break-word;
}

@media print {
  .no-print {
    display: none !important;
  }

  body {
    background: white !important;
  }

  .audit-log-report {
    background: white !important;
    padding: 0 !important;
  }

  .report-page {
    box-shadow: none !important;
    border: none !important;
    padding: 0 !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
  }

  .report-table {
    font-size: 9pt !important;

    th,
    td {
      padding: 5px !important;
    }
  }

  .btn-modern,
  .control-bar {
    display: none !important;
  }
}
</style>
